import { decode } from '@jridgewell/sourcemap-codec';

export interface MappedFrame {
  originalFile: string;
  originalLine: number;
  originalColumn: number;
  functionName?: string;
  componentName?: string;
  sourceSnippet?: string;
  rawLine: string;
  isUserCode: boolean;
}

export interface MappedErrorInfo {
  primaryLocation: MappedFrame | null;
  mappedStack: string;
  mappedComponentStack: string;
  userFrames: MappedFrame[];
}

interface RawSourceMap {
  version: number;
  sources: string[];
  sourcesContent?: (string | null)[];
  mappings: string;
  names?: string[];
  file?: string;
}

interface DecodedCacheItem {
  map: RawSourceMap;
  decodedMappings: number[][][];
}

const sourceMapCache = new Map<string, Promise<DecodedCacheItem | null>>();

function cleanUrl(rawUrl: string): string {
  try {
    const urlObj = new URL(rawUrl, window.location.origin);
    urlObj.search = '';
    urlObj.hash = '';
    return urlObj.href;
  } catch {
    return rawUrl.split('?')[0].split('#')[0];
  }
}

function cleanSourcePath(sourcePath: string): string {
  let cleaned = sourcePath.replace(/\\/g, '/');
  // Strip common prefixes
  const srcIdx = cleaned.indexOf('/src/');
  if (srcIdx !== -1) {
    return cleaned.substring(srcIdx + 1);
  }
  if (cleaned.startsWith('src/')) {
    return cleaned;
  }
  cleaned = cleaned.replace(/^(\.\.\/)+/, '').replace(/^\.\//, '');
  return cleaned;
}

function extractSnippet(sourceContent: string, targetLine: number, contextLines: number = 3): string {
  const lines = sourceContent.split('\n');
  const start = Math.max(0, targetLine - 1 - contextLines);
  const end = Math.min(lines.length, targetLine + contextLines);
  const padLength = String(end).length;

  const result: string[] = [];
  for (let i = start; i < end; i++) {
    const lineNum = i + 1;
    const isTarget = lineNum === targetLine;
    const paddedNum = String(lineNum).padStart(padLength, ' ');
    const marker = isTarget ? '>' : ' ';
    result.push(`${marker} ${paddedNum} | ${lines[i]}`);
  }
  return result.join('\n');
}

async function fetchSourceMapForUrl(url: string): Promise<DecodedCacheItem | null> {
  const normalized = cleanUrl(url);

  if (sourceMapCache.has(normalized)) {
    return sourceMapCache.get(normalized)!;
  }

  const promise = (async (): Promise<DecodedCacheItem | null> => {
    try {
      const resp = await fetch(url, { credentials: 'same-origin' });
      if (!resp.ok) return null;
      const text = await resp.text();

      let sourceMapJson: RawSourceMap | null = null;

      // 1. Look for inline base64 sourcemap
      const inlineMatch = text.match(/\/\/# sourceMappingURL=data:application\/json;base64,([A-Za-z0-9+/=]+)/);
      if (inlineMatch && inlineMatch[1]) {
        try {
          const decodedJson = atob(inlineMatch[1]);
          sourceMapJson = JSON.parse(decodedJson);
        } catch (e) {
          console.warn('[sourceMapResolver] Failed to parse inline base64 sourcemap:', e);
        }
      }

      // 2. Look for separate sourcemap comment: //# sourceMappingURL=filename.map
      if (!sourceMapJson) {
        const mapUrlMatch = text.match(/\/\/# sourceMappingURL=([^\s]+)/);
        if (mapUrlMatch && mapUrlMatch[1]) {
          try {
            const mapAbsoluteUrl = new URL(mapUrlMatch[1], url).href;
            const mapResp = await fetch(mapAbsoluteUrl, { credentials: 'same-origin' });
            if (mapResp.ok) {
              sourceMapJson = await mapResp.json();
            }
          } catch (e) {
            console.warn('[sourceMapResolver] Failed to fetch external sourcemap:', e);
          }
        }
      }

      // 3. Fallback for .js files: try fetching `${url}.map`
      if (!sourceMapJson && normalized.endsWith('.js')) {
        try {
          const mapResp = await fetch(`${normalized}.map`, { credentials: 'same-origin' });
          if (mapResp.ok) {
            sourceMapJson = await mapResp.json();
          }
        } catch {
          // ignore fallback failure
        }
      }

      if (!sourceMapJson || !sourceMapJson.mappings) {
        return null;
      }

      const decodedMappings = decode(sourceMapJson.mappings);
      return {
        map: sourceMapJson,
        decodedMappings,
      };
    } catch (e) {
      console.warn('[sourceMapResolver] Failed to resolve sourcemap for:', url, e);
      return null;
    }
  })();

  sourceMapCache.set(normalized, promise);
  return promise;
}

interface ParsedStackLine {
  originalText: string;
  prefix: string;
  functionName?: string;
  componentName?: string;
  url?: string;
  line?: number;
  col?: number;
}

function parseStackLine(line: string): ParsedStackLine {
  const result: ParsedStackLine = { originalText: line, prefix: '' };
  const trimmed = line.trim();

  // Pattern 1: at FunctionName (http://...:line:col)
  let match = trimmed.match(/^at\s+(?:async\s+)?([^\s(]+)?\s*\((https?:\/\/[^:]+|(?:\/|[a-zA-Z]:\\)[^:]+):(\d+):(\d+)\)$/);
  if (match) {
    result.prefix = line.substring(0, line.indexOf(trimmed));
    result.functionName = match[1];
    result.url = match[2];
    result.line = parseInt(match[3], 10);
    result.col = parseInt(match[4], 10);
    return result;
  }

  // Pattern 2: at http://...:line:col
  match = trimmed.match(/^at\s+(https?:\/\/[^:]+|(?:\/|[a-zA-Z]:\\)[^:]+):(\d+):(\d+)$/);
  if (match) {
    result.prefix = line.substring(0, line.indexOf(trimmed));
    result.url = match[1];
    result.line = parseInt(match[2], 10);
    result.col = parseInt(match[3], 10);
    return result;
  }

  // Pattern 3: React component stack "in ComponentName (at Component.tsx:75)"
  match = trimmed.match(/^in\s+([^\s(]+)\s*\((?:at\s+)?(https?:\/\/[^:]+|[^\s:()]+\.[a-zA-Z0-9]+):(\d+)(?::(\d+))?\)$/);
  if (match) {
    result.prefix = line.substring(0, line.indexOf(trimmed));
    result.componentName = match[1];
    result.url = match[2];
    result.line = parseInt(match[3], 10);
    result.col = match[4] ? parseInt(match[4], 10) : 1;
    return result;
  }

  // Pattern 4: React 18 component stack "at ComponentName (http://...:line:col)"
  match = trimmed.match(/^at\s+([A-Z][A-Za-z0-9_]*)\s*\((https?:\/\/[^:]+):(\d+):(\d+)\)$/);
  if (match) {
    result.prefix = line.substring(0, line.indexOf(trimmed));
    result.componentName = match[1];
    result.url = match[2];
    result.line = parseInt(match[3], 10);
    result.col = parseInt(match[4], 10);
    return result;
  }

  return result;
}

export async function mapSingleFrame(parsed: ParsedStackLine): Promise<MappedFrame | null> {
  if (!parsed.url || !parsed.line) return null;

  // Resolve absolute URL if relative
  let fetchUrl = parsed.url;
  if (!fetchUrl.startsWith('http://') && !fetchUrl.startsWith('https://')) {
    if (fetchUrl.startsWith('/')) {
      fetchUrl = window.location.origin + fetchUrl;
    } else {
      fetchUrl = window.location.origin + '/' + fetchUrl;
    }
  }

  const isUserCode =
    fetchUrl.includes('/src/') ||
    (!fetchUrl.includes('node_modules') && !fetchUrl.includes('.vite/deps'));

  const item = await fetchSourceMapForUrl(fetchUrl);
  if (!item) {
    // If no sourcemap, but URL contains a direct TypeScript file in dev mode
    const clean = cleanSourcePath(parsed.url);
    if (clean.endsWith('.tsx') || clean.endsWith('.ts')) {
      return {
        originalFile: clean,
        originalLine: parsed.line,
        originalColumn: parsed.col || 1,
        functionName: parsed.functionName,
        componentName: parsed.componentName,
        rawLine: parsed.originalText,
        isUserCode,
      };
    }
    return null;
  }

  const { map, decodedMappings } = item;
  const lineIdx = parsed.line - 1;
  if (lineIdx < 0 || lineIdx >= decodedMappings.length) return null;

  const segments = decodedMappings[lineIdx];
  if (!segments || segments.length === 0) return null;

  const targetCol = (parsed.col || 1) - 1;
  // Find segment with greatest generatedColumn <= targetCol
  let chosen = segments[0];
  for (let i = 1; i < segments.length; i++) {
    if (segments[i][0] <= targetCol) {
      chosen = segments[i];
    } else {
      break;
    }
  }

  if (chosen.length < 4) return null;

  const sourceIdx = chosen[1];
  const origLine = chosen[2] + 1;
  const origCol = chosen[3] + 1;
  const rawSource = map.sources[sourceIdx] || parsed.url;
  const originalFile = cleanSourcePath(rawSource);

  let sourceSnippet: string | undefined;
  if (map.sourcesContent && map.sourcesContent[sourceIdx]) {
    const content = map.sourcesContent[sourceIdx];
    if (content) {
      sourceSnippet = extractSnippet(content, origLine, 3);
    }
  }

  return {
    originalFile,
    originalLine: origLine,
    originalColumn: origCol,
    functionName: parsed.functionName,
    componentName: parsed.componentName,
    sourceSnippet,
    rawLine: parsed.originalText,
    isUserCode: isUserCode || originalFile.includes('src/'),
  };
}

export async function resolveErrorSourceMap(
  error: Error,
  componentStack?: string | null
): Promise<MappedErrorInfo> {
  const stackLines = (error.stack || '').split('\n');
  const compLines = (componentStack || '').split('\n');

  const mappedStackLines: string[] = [];
  const mappedCompLines: string[] = [];
  const userFrames: MappedFrame[] = [];

  // 1. Process Error Stack
  for (const line of stackLines) {
    const parsed = parseStackLine(line);
    if (!parsed.url || !parsed.line) {
      mappedStackLines.push(line);
      continue;
    }

    try {
      const mapped = await mapSingleFrame(parsed);
      if (mapped) {
        if (mapped.isUserCode) {
          userFrames.push(mapped);
        }
        const fn = mapped.functionName || parsed.functionName;
        const fnPart = fn ? ` ${fn}` : '';
        mappedStackLines.push(
          `${parsed.prefix || '    '}at${fnPart} (${mapped.originalFile}:${mapped.originalLine}:${mapped.originalColumn})`
        );
      } else {
        mappedStackLines.push(line);
      }
    } catch {
      mappedStackLines.push(line);
    }
  }

  // 2. Process Component Stack
  for (const line of compLines) {
    const parsed = parseStackLine(line);
    if (!parsed.url || !parsed.line) {
      mappedCompLines.push(line);
      continue;
    }

    try {
      const mapped = await mapSingleFrame(parsed);
      if (mapped) {
        if (mapped.isUserCode) {
          userFrames.push(mapped);
        }
        const comp = mapped.componentName || parsed.componentName || 'Component';
        mappedCompLines.push(
          `${parsed.prefix || '    '}in ${comp} (at ${mapped.originalFile}:${mapped.originalLine}:${mapped.originalColumn})`
        );
      } else {
        mappedCompLines.push(line);
      }
    } catch {
      mappedCompLines.push(line);
    }
  }

  // 3. Find the primary user code location
  // Prefer the first frame that is in user code (usually the deepest point where our code triggered the error)
  let primaryLocation: MappedFrame | null = null;
  if (userFrames.length > 0) {
    primaryLocation = userFrames[0];
  }

  return {
    primaryLocation,
    mappedStack: mappedStackLines.join('\n'),
    mappedComponentStack: mappedCompLines.join('\n'),
    userFrames,
  };
}

export function formatFullErrorReport(options: {
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  mappedInfo: MappedErrorInfo | null;
}): string {
  const { error, errorInfo, mappedInfo } = options;
  const sections: string[] = [];

  sections.push(`### Báo cáo chi tiết lỗi (Error Report)`);
  sections.push(`- **Thời gian**: ${new Date().toLocaleString()}`);
  sections.push(`- **Địa chỉ**: ${window.location.href}`);
  sections.push(`- **Lỗi**: \`${error?.name || 'Error'}\`: ${error?.message || 'Không có thông điệp'}`);

  if (mappedInfo?.primaryLocation) {
    const loc = mappedInfo.primaryLocation;
    sections.push(
      `- **Vị trí lỗi TypeScript**: \`${loc.originalFile}:${loc.originalLine}:${loc.originalColumn}\`${
        loc.componentName ? ` (Component: \`<${loc.componentName} />\`)` : ''
      }`
    );

    if (loc.sourceSnippet) {
      sections.push(`\n#### Đoạn mã TypeScript phát sinh lỗi:`);
      sections.push('```tsx\n' + loc.sourceSnippet + '\n```');
    }
  }

  const stackToUse = mappedInfo?.mappedStack || error?.stack;
  if (stackToUse) {
    sections.push(`\n#### Stack trace (TypeScript Mapped):`);
    sections.push('```\n' + stackToUse + '\n```');
  }

  const compStackToUse = mappedInfo?.mappedComponentStack || errorInfo?.componentStack;
  if (compStackToUse) {
    sections.push(`\n#### Component hierarchy stack:`);
    sections.push('```\n' + compStackToUse + '\n```');
  }

  return sections.join('\n');
}
